import produce from "immer";
import { useState } from "react";

import {
  ConnectionBaseFragment,
  IntegrationAbility,
  SubscriptionStatus,
} from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import { Heading, SectionHeading } from "@/components/elements/Typography";
import { useStepsContext } from "@/components/modules/stepper";
import { useConnectorOptions } from "@/features/connectors";
import { useSubscriptionStatus } from "@/features/subscription";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useToast } from "@/providers/ToastProvider";
import {
  ConnectorOption,
  Connectors,
  SearchFilter,
} from "@/widgets/connectors/connectors-list";
import { NewConnectionSlideOver } from "@/widgets/connectors/new-connector-slide-over";
import { RequestConnector } from "@/widgets/request-connector";

import { useReverseETLSetupContext } from "../../state/ReverseETLSetupContext";
import {
  createInitialState,
  useReverseETLStateContext,
} from "../../state/StepStateContext";
import { EndpointBadge } from "../model-mapping/ui/components/EndpointBadge";

export function DestinationStep() {
  const { state, setState, initialFormState } = useReverseETLStateContext();
  const stepsContext = useReverseETLSetupContext();

  const dwh = useDataWarehouseContext();
  const options = useConnectorOptions({ dwIntegration: dwh.integration });
  const { status } = useSubscriptionStatus();
  const { onGoToStep } = useStepsContext();
  const toast = useToast();

  const [integrationId, setIntegrationId] = useState<string | undefined>(() => {
    if (state.destination.value?.connection) {
      // This prevents the new-connection slide over from opening when the user
      // navigates back to the destination step
      return undefined;
    }
    return state.destination.value?.integrationId;
  });

  const handleConnectionSelected = async (
    connection: ConnectionBaseFragment,
  ) => {
    setState(
      produce(state, (draft) => {
        const destinationValues = {
          value: {
            integrationId: connection.integrationId,
            connectionId: connection.id,
            connection,
          },
          isValid: true,
          isDirty: false,
        };

        const currentConnectionId = state.destination.value?.connection?.id;
        if (currentConnectionId && currentConnectionId !== connection.id) {
          // Changing connection means we need to reset state in all other steps
          const newState = createInitialState();
          newState.destination = destinationValues;
          return newState;
        } else {
          draft.destination = destinationValues;
        }
      }),
    );

    const connectionSettingsResult = await stepsContext.getConnectionSettings(
      connection.id,
    );

    if (
      connectionSettingsResult.connectionSettings == null &&
      connectionSettingsResult.error
    ) {
      toast(
        `Connection failed for "${connection.label}"`,
        connectionSettingsResult.error.message,
        "error",
      );
      onGoToStep("destination");
      // Reset the state to clear the connection
      setState(createInitialState());
      return;
    }

    stepsContext.onSetupStarted(connection);

    if (connectionSettingsResult.connectionSettings == null) {
      onGoToStep("model-mapping");
    } else {
      onGoToStep("destination-settings");
    }
  };

  return (
    <>
      <div className="mb-6">
        <Heading className="mb-1">Choose a Destination</Heading>
        {initialFormState?.modelMapping?.source != null ? (
          <div className="text-sm text-muted-foreground">
            You’re setting up a sync from{" "}
            <EndpointBadge
              integrationId={
                initialFormState.modelMapping.source.integrationId ??
                dwh.integration.id
              }
            >
              {initialFormState.modelMapping.source.label}
            </EndpointBadge>
            . Choose a destination platform below to continue.
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select the platform where you want your data to be delivered. Pick
            from an existing connection or set up a new one to get started.
          </p>
        )}
      </div>
      <Connectors
        options={options}
        enableSelectExistingConnection
        abilityFilter={IntegrationAbility.ActivateDestination}
        // Return existing connection immediately
        onSelectConnection={handleConnectionSelected}
        // Otherwise, open the connection configuration
        onSelectIntegration={(option) => {
          setIntegrationId(option.integration.id);
        }}
      >
        {({ options }) => (
          <div className="flex w-full flex-1 flex-col items-center gap-6">
            <div className="w-full py-1">
              <SearchFilter autoFocus />
            </div>
            <div className="flex w-full flex-col gap-6 pt-1">
              <div>
                <SectionHeading className="mb-2 text-xs font-semibold leading-6">
                  All Destinations
                </SectionHeading>
                <div className="grid gap-4 sm:grid-cols-2">
                  {options.map((option) => (
                    <ConnectorOption
                      key={option.value}
                      option={option}
                      showAbilityLabels={false}
                      showPlanRequirement={
                        status === SubscriptionStatus.Freemium
                      }
                    />
                  ))}
                </div>
              </div>
              <div className="my-8 flex flex-col items-center">
                <p className="mb-2 block text-center text-muted-foreground">
                  Didn't find what you are looking for?
                </p>
                <RequestConnector
                  defaultValues={{ reverseEtl: true, elt: false }}
                />
              </div>
            </div>
          </div>
        )}
      </Connectors>
      <NewConnectionSlideOver
        show={!!integrationId}
        integrationId={integrationId}
        onClose={() => {
          setIntegrationId(undefined);
        }}
        onConnectionAdded={handleConnectionSelected}
      />
      {stepsContext.isLoadingConnectionSettings && (
        <div className="absolute left-1/2 top-1/2 z-10 flex w-full max-w-48 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-lg border bg-background/95 p-8 shadow-2xl backdrop-blur supports-[backdrop-filter]:bg-background/50">
          <div className="flex items-center gap-4">
            <LoadingSpinner className="h-6 w-6" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        </div>
      )}
    </>
  );
}
